import { React, useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Suspense } from "react";

import Container from "react-bootstrap/Container";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";

import { getIndividualStudentResults } from "../services/APIClient";
import { BigSpinner } from "../services/SpinnerComponent";

import "./IndividualStudentResultsOverview.css";
import "./ClassOverview.css";
import "./PrintStudentResults.css";

import ReturnToResults from "./ReturnToResults";
import ReturnToClassResults from "./RetrunToClassResults";
import SENWatchlistButton from "./SENWatchlistButton";
import ResultsHistory from "./ResultsHistory";
import RecommendationsButton from "./RecommendationsButton";

import ScoreInterpretationTable from "./ScoreInterpretationTable";

import PrintActionIndividual from "./PrintActionIndividual";
import ShareActionCard from "./ShareActionCard";
import { preprocessStudentResults } from "../services/GroupAndAverageTasks";

function IndividualStudentResultsOverview(props) {
  const {
    classDetails,
    classAssessments,
    gradeLevel,
    teachers,
    assessment,
    student,
    assessmentResultsByStudent,
    schoolid,
    teacherid,
    fromSencoResults,
    readingScores,
  } = props;

  console.log(assessment, "ASSESSMENT PROPS");
  
  // DEBUG: Log assessment name to see what we're filtering against
  console.log("=== ASSESSMENT NAME DEBUG ===");
  console.log("assessment.name:", assessment.name);
  console.log("Full assessment object:", assessment);

  // All hooks must be called before any early return
  const [individualStudentResult, setIndividualStudentResult] = useState([]);
  const [toggleScoreType, setToggleScoreType] = useState("");
  const [grade1Results, setGrade1Results] = useState([]);
  const [grade2Results, setGrade2Results] = useState([]);
  const [grade3Results, setGrade3Results] = useState([]);
  const [allResults, setAllResults] = useState([]); // Added for all results
  const [grade1Prefix, setGrade1Prefix] = useState("");
  const [grade2Prefix, setGrade2Prefix] = useState("");
  const [grade3Prefix, setGrade3Prefix] = useState("");
  const [showCurrentOnly, setShowCurrentOnly] = useState(true);
  const [loading, setLoading] = useState(true); // Added loading state
  const [activeTab, setActiveTab] = useState(() => {
    if (assessment.grade_level1) return "grade1";
    if (assessment.grade_level2) return "grade2";
    if (assessment.grade_level3) return "grade3";
    return "all"; // Default to "all" if neither grade level matches
  });
  const [toggleChanged, setToggleChanged] = useState(false); // Track if toggle has been changed due to tab switch
  const [showPrintCard, setShowPrintCard] = useState(false);
  const [printCardPosition, setPrintCardPosition] = useState({
    top: 0,
    left: 0,
  });
  const [showShareCard, setShowShareCard] = useState(false);
  const [shareCardPosition, setShareCardPosition] = useState({
    top: 0,
    left: 0,
  });
  const [viewParent, setViewParent] = useState(false);
  const [viewTeacher, setViewTeacher] = useState(false);

    useEffect(() => {
  console.log("Applying sorting...");
  // Force a re-render after a short delay to ensure sorting is applied
  if (allResults.length > 0) {
    const timer = setTimeout(() => {
      console.log("Forcing UI update for sorted results");
      setActiveTab(prev => prev); // This forces a re-render
    }, 100);
    return () => clearTimeout(timer);
  }
}, [allResults]);

  useEffect(() => {
    if (!student || !student.id) return; // Don't fetch if no student
    setLoading(true);
    getIndividualStudentResults(student.id)
      .then((response) => {
        setIndividualStudentResult(response);

        // DEBUG: Log raw response to see what assessment names exist
        console.log("=== RAW API RESPONSE ===");
        console.log("Raw response:", response);
        console.log("Assessment names in response:", response.map(r => r.name));

        const processedResults = preprocessStudentResults(response);
        
        // DEBUG: Log processed results to see what assessment names exist after processing
        console.log("=== PROCESSED RESULTS ===");
        console.log("Processed results:", processedResults);
        console.log("Assessment names in processed results:", processedResults.map(r => r.name));
        
        setAllResults(processedResults); // Store all processed results

        const grade1 = processedResults.filter(
          (result) => result.grade_level1
        );
        const grade2 = processedResults.filter(
          (result) => result.grade_level2
        );
        const grade3 = processedResults.filter(
          (result) => result.grade_level3
        );
        
        // DEBUG: Log grade-specific results
        console.log("=== GRADE SPECIFIC RESULTS ===");
        console.log("Grade 1 results:", grade1);
        console.log("Grade 1 assessment names:", grade1.map(r => r.name));
        console.log("Grade 2 results:", grade2);
        console.log("Grade 2 assessment names:", grade2.map(r => r.name));
         console.log("Grade 3 results:", grade3);
        console.log("Grade 3 assessment names:", grade3.map(r => r.name));

        setGrade1Results(grade1);
        setGrade2Results(grade2);
        setGrade3Results(grade3);

        if (grade1.length > 0) {
          setGrade1Prefix(grade1[0].name.split(" ")[0]);
        }
        if (grade2.length > 0) {
          setGrade2Prefix(grade2[0].name.split(" ")[0]);
        }
         if (grade3.length > 0) {
          setGrade3Prefix(grade3[0].name.split(" ")[0]);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [student?.id]); // Only run if student.id changes

  // Function to sort assessment types (baseline, midpoint, etc.)
  const sortByAssessmentType = (results) => {
    return [...results].sort((a, b) => {
      const getSortKey = (name) => {
        name = name.toLowerCase();
        if (name.includes("baseline")) return 1;
        if (name.includes("midpoint")) return 2;
        if (name.includes("end of year")) return 3;
        return 4; // Default for other assessments
      };
      
      return getSortKey(a.name) - getSortKey(b.name);
    });
  };

  // Guard: Ensure student is defined and has id (after hooks)
  if (!student || !student.id) {
    return (
      <div>
        <h3>No student selected or student data missing.</h3>
      </div>
    );
  }

  const { id: studentid, name: studentName } = student;
  const {
    grade_level1: gradeLevel1,
    grade_level2: gradeLevel2,
    grade_level3: gradeLevel3,
    trial_class: trialClass,
  } = classDetails;

  // DEBUG: Add logging for the filtering process
  console.log("=== FILTERING DEBUG ===");
  console.log("showCurrentOnly:", showCurrentOnly);
  console.log("assessment.name for filtering:", assessment.name);
  
  // Grade abbreviation mapping
  const gradeAbbreviations = {
    "Standard 1": "S1",
    "Standard 2": "S2",
    Reception: "Recep",
    "Year 1": "Y1",
    "Primary 1": "P1",
    "Primary 2": "P2",
    "Pre-K": "Pre-K",
    "Kindergarten (K)": "K",
    "KG1 (Kindergarten 1)": "KG1",
    "KG2 (Kindergarten 2)": "KG2",
    "Junior": "Jnr",
    "Senior": "Snr",
    "1st": "1st",
  };

  // Helper function to check if a result matches the current assessment
  const isMatchingAssessment = (resultName, assessmentName) => {
    // Exact match
    if (resultName === assessmentName) return true;
    
    // Remove " Instance" suffix from result name for comparison
    const cleanResultName = resultName.replace(/\s+Instance$/, '');
    
    // Check if clean result name matches assessment name
    if (cleanResultName === assessmentName) return true;
    
    // Try abbreviating the assessment name using the grade mapping
    let abbreviatedAssessmentName = assessmentName;
    Object.entries(gradeAbbreviations).forEach(([fullGrade, abbreviation]) => {
      if (assessmentName.includes(fullGrade)) {
        abbreviatedAssessmentName = assessmentName.replace(fullGrade, abbreviation);
      }
    });
    
    // Check if result name matches the abbreviated assessment name
    if (cleanResultName === abbreviatedAssessmentName) return true;
    
    // Try expanding abbreviated grade names in result to full names
    let expandedResultName = cleanResultName;
    Object.entries(gradeAbbreviations).forEach(([fullGrade, abbreviation]) => {
      if (cleanResultName.includes(abbreviation)) {
        expandedResultName = cleanResultName.replace(abbreviation, fullGrade);
      }
    });
    
    // Check if expanded result name matches assessment name
    if (expandedResultName === assessmentName) return true;
    
    console.log(`Matching attempts for "${resultName}" vs "${assessmentName}":`);
    console.log(`  Clean result: "${cleanResultName}"`);
    console.log(`  Abbreviated assessment: "${abbreviatedAssessmentName}"`);
    console.log(`  Expanded result: "${expandedResultName}"`);
    
    return false;
  };

  const filteredGrade1Results = showCurrentOnly
    ? grade1Results.filter((result) => {
        const matches = isMatchingAssessment(result.name, assessment.name);
        console.log(`Grade1 - ${result.name} matches ${assessment.name}? ${matches}`);
        return matches;
      })
    : grade1Results;

  const filteredGrade2Results = showCurrentOnly
    ? grade2Results.filter((result) => {
        const matches = isMatchingAssessment(result.name, assessment.name);
        console.log(`Grade2 - ${result.name} matches ${assessment.name}? ${matches}`);
        return matches;
      })
    : grade2Results;
  
  const filteredGrade3Results = showCurrentOnly
    ? grade3Results.filter((result) => {
        const matches = isMatchingAssessment(result.name, assessment.name);
        console.log(`Grade3 - ${result.name} matches ${assessment.name}? ${matches}`);
        return matches;
      })
    : grade3Results;

  const filteredAllResults = showCurrentOnly
    ? allResults.filter((result) => {
        const matches = isMatchingAssessment(result.name, assessment.name);
        console.log(`All - ${result.name} matches ${assessment.name}? ${matches}`);
        return matches;
      })
    : allResults;

  // DEBUG: Log filtered results counts
  console.log("=== FILTERED RESULTS COUNTS ===");
  console.log("Filtered Grade1 Results count:", filteredGrade1Results.length);
  console.log("Filtered Grade2 Results count:", filteredGrade2Results.length);
  console.log("Filtered Grade3 Results count:", filteredGrade3Results.length);
  console.log("Filtered All Results count:", filteredAllResults.length);

  // More explicit approach for sorting "All Results"
  const sortedAllResults = [
    ...filteredAllResults.filter(result => result.grade_level1).sort((a, b) => {
      const getSortKey = name => {
        name = name.toLowerCase();
        if (name.includes("baseline")) return 1;
        if (name.includes("midpoint")) return 2;
        if (name.includes("end of year")) return 3;
        return 4;
      };
      return getSortKey(a.name) - getSortKey(b.name);
    }),
    ...filteredAllResults.filter(result => !result.grade_level1 && result.grade_level2).sort((a, b) => {
      const getSortKey = name => {
        name = name.toLowerCase();
        if (name.includes("baseline")) return 1;
        if (name.includes("midpoint")) return 2;
        if (name.includes("end of year")) return 3;
        return 4;
      };
      return getSortKey(a.name) - getSortKey(b.name);
    })
  ];

  // Sort grade1 and grade2 results separately
  const sortedGrade1Results = sortByAssessmentType(filteredGrade1Results);
  const sortedGrade2Results = sortByAssessmentType(filteredGrade2Results);
  const sortedGrade3Results = sortByAssessmentType(filteredGrade3Results);

  const filteredIndividualResults = showCurrentOnly
    ? preprocessStudentResults(individualStudentResult).filter(
        (result) => {
          const matches = isMatchingAssessment(result.name, assessment.name);
          console.log(`Individual - ${result.name} matches ${assessment.name}? ${matches}`);
          return matches;
        }
      )
    : preprocessStudentResults(individualStudentResult);

  // Sort the individual results with the same explicit approach
  const sortedIndividualResults = [
    ...filteredIndividualResults.filter(result => result.grade_level1).sort((a, b) => {
      const getSortKey = name => {
        name = name.toLowerCase();
        if (name.includes("baseline")) return 1;
        if (name.includes("midpoint")) return 2;
        if (name.includes("end of year")) return 3;
        return 4;
      };
      return getSortKey(a.name) - getSortKey(b.name);
    }),
    ...filteredIndividualResults.filter(result => !result.grade_level1 && result.grade_level2).sort((a, b) => {
      const getSortKey = name => {
        name = name.toLowerCase();
        if (name.includes("baseline")) return 1;
        if (name.includes("midpoint")) return 2;
        if (name.includes("end of year")) return 3;
        return 4;
      };
      return getSortKey(a.name) - getSortKey(b.name);
    })
  ];

  // Toggle function
  const toggleView = () => {
    setShowCurrentOnly(!showCurrentOnly);
    
    // DEBUG: Log toggle change
    console.log("=== TOGGLE CHANGED ===");
    console.log("New showCurrentOnly value:", !showCurrentOnly);
  };

  const handleTabSelect = (key) => {
    setActiveTab(key);
    if (!toggleChanged) {
      setShowCurrentOnly(!showCurrentOnly); // Change the toggle state
      setToggleChanged(true); // Ensure this only happens once
    }
  };

  console.log(allResults, "ALL RESULTS");
  console.log(sortedAllResults, "SORTED ALL RESULTS");
  console.log(grade1Results, "GRADE 1 RESULTS");  
  console.log(sortedGrade1Results, "SORTED GRADE 1 RESULTS");
  console.log(grade2Results, "GRADE 2 RESULTS");
  console.log(sortedGrade2Results, "SORTED GRADE 2 RESULTS");
  console.log(grade3Results, "GRADE 3 RESULTS");
  console.log(sortedGrade3Results, "SORTED GRADE 3 RESULTS");
  console.log(individualStudentResult, "INDIVIDUAL STUDENT RESULT");
  console.log(sortedIndividualResults, "SORTED INDIVIDUAL STUDENT RESULT");

  if (loading) {
    return <BigSpinner />;
  }

  const shareActiveResults =
    activeTab === "grade1" ? sortedGrade1Results :
    activeTab === "grade2" ? sortedGrade2Results :
    activeTab === "grade3" ? sortedGrade3Results :
    sortedAllResults;

  const shareAssessmentIds = [...new Set(
    shareActiveResults.map(r => r.assessmentid).filter(Boolean)
  )];
  const shareAssessmentName = [...new Set(
    shareActiveResults.map(r => r.name).filter(Boolean)
  )].join(" & ") || assessment?.name || "Assessment";

  return (
    <div>
      <style>
        {`
          @media print {
            .alpaca-print-footer {
              display: block !important;
            }
          }
        `}
      </style>
      <Row className="inline-block align-items-center push-down">
        <Col className="col-auto">
          <Suspense fallback={<div>Loading Navigation Button...</div>}>
            <ReturnToClassResults
              classDetails={classDetails}
              teachers={teachers}
              classAssessments={classAssessments}
              assessment={assessment}
              schoolid={schoolid}
              teacherid={teacherid}
              fromSencoResults={fromSencoResults}
            />
          </Suspense>
        </Col>
        <Col className="flex-grow-1 text-center" style={{ margin: "10px" }}>
          <h2 className="page-description-header-results">Results Overview</h2>
        </Col>
        <Col className="col-auto d-flex justify-content-end">
          {fromSencoResults && (
            <SENWatchlistButton
              schoolid={schoolid}
              teachers={teachers}
              teacherid={teacherid}
              assessment={assessment}
              readingScores={readingScores}
            />
          )}
          <button
            className="print-button"
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              setPrintCardPosition({
                top: rect.top + window.scrollY,
                left: rect.left - 250,
              });
              setShowPrintCard(!showPrintCard);
            }}
          >
            Print
            <img
              src={process.env.PUBLIC_URL + "/assets/imgs/chevron-sort.svg"}
              alt="Sort"
              className="print-button-icon"
            />
          </button>
          <button
            className="print-button"
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              setShareCardPosition({
                top: rect.top + window.scrollY,
                left: rect.left - 250,
              });
              setShowShareCard(!showShareCard);
            }}
          >
            Share
            <img
              src={process.env.PUBLIC_URL + "/assets/imgs/chevron-sort.svg"}
              alt="Sort"
              className="print-button-icon"
            />
          </button>
        </Col>
      </Row>
      <div className="class-overview-container">
        <Container className="container-shape">
          <Row>
            <Col className="col-3 pr-2">
              {" "}
              <h3 className="report-card-header">
                Report Card: {student.name}
              </h3>
            </Col>
          </Row>
          <Row className="page-description-bar align-items-center">
            <Col className="col-auto">
              <Suspense
                fallback={<div>Loading Assessment Dropdown...</div>}
              ></Suspense>
            </Col>
            <Col className="d-flex justify-content-end align-items-center">
              {/* <Suspense fallback={<div>Loading Recommendations...</div>}>
                <RecommendationsButton onResultsPage={true} />
              </Suspense> */}
              <Suspense fallback={<div>Loading score table...</div>}>
                <ScoreInterpretationTable />
              </Suspense>
            </Col>
          </Row>

          <Row>
            <div className="toggle-container">
              <h5 className="page-description-header">
                Toggle between All Assessments and Current{" "}
              </h5>
              <label className="switch">
                <input
                  type="checkbox"
                  onChange={toggleView}
                  checked={showCurrentOnly}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </Row>
          <Row className="button-container">
            <Col className="col-2"></Col>
          </Row>

          <Row className="button-container">
            <Col className="col-2">
              {/* <GradeCard gradeLevel={gradeLevel} /> */}
            </Col>
            <Col className="col-2">
              {/* <ScoreToggleButton toggleScoreType={toggleScoreType} /> */}
            </Col>
          </Row>

          <br></br>
          {allResults.length > 0 ? (
            <Tabs
              defaultActiveKey={activeTab}
              id="grade-tabs"
              className="custom-tabs"
              activeKey={activeTab}
              onSelect={handleTabSelect}
              style={{ marginBottom: "16px" }}
            >
              {/* All tab */}
              <Tab eventKey="all" title="All Results">
                <Row>
                  <Col className="col-3"></Col>
                  <Col className="col-8"></Col>
                </Row>
                {allResults.length > 0 ? (
                  <ResultsHistory
                    individualStudentResult={sortedAllResults} // Use sorted version here
                    toggleScoreType={toggleScoreType}
                    assessmentResultsByStudent={assessmentResultsByStudent}
                    isPrintOut={false}
                    detailedView={true}
                    studentName={studentName}
                    teacherid={teacherid}
                    schoolid={schoolid}
                    assessment={assessment}
                    studentid={studentid}
                    key={`results-${activeTab}-${allResults.length}`}
                  />
                ) : (
                  <Row>
                    <Col>
                      <p>No answers registered for this student.</p>
                    </Col>
                  </Row>
                )}
              </Tab>

              {/* Only show Grade 1 tab if there are Grade 1 results */}
              {grade1Results.length > 0 && (
                <Tab eventKey="grade1" title={`${grade1Prefix} Level Results`}>
                  <Row>
                    <Col className="col-3"></Col>
                    <Col className="col-3"></Col>
                    <Col className="col-8"></Col>
                  </Row>
                  <ResultsHistory
                    individualStudentResult={sortedGrade1Results} // Use sorted version
                    toggleScoreType={toggleScoreType}
                    assessmentResultsByStudent={assessmentResultsByStudent}
                    isPrintOut={false}
                    detailedView={true}
                    studentName={studentName}
                    teacherid={teacherid}
                    schoolid={schoolid}
                    assessment={assessment}
                    studentid={studentid}
                    key={`results-${activeTab}-${allResults.length}`}
                  />
                </Tab>
              )}

              {/* Only show Grade 2 tab if there are Grade 2 results */}
              {grade2Results.length > 0 && (
                <Tab eventKey="grade2" title={`${grade2Prefix} Level Results`}>
                  <Row>
                    <Col className="col-3"></Col>
                    <Col className="col-8"></Col>
                  </Row>
                  <ResultsHistory
                    individualStudentResult={sortedGrade2Results} // Use sorted version
                    toggleScoreType={toggleScoreType}
                    assessmentResultsByStudent={assessmentResultsByStudent}
                    isPrintOut={false}
                    detailedView={true}
                    studentName={studentName}
                    teacherid={teacherid}
                    schoolid={schoolid}
                    assessment={assessment}
                    studentid={studentid}
                    key={`results-${activeTab}-${allResults.length}`}

                  />
                </Tab>
              )}
              {grade3Results.length > 0 && (
                <Tab eventKey="grade2" title={`${grade3Prefix} Level Results`}>
                  <Row>
                    <Col className="col-3"></Col>
                    <Col className="col-8"></Col>
                  </Row>
                  <ResultsHistory
                    individualStudentResult={sortedGrade3Results} // Use sorted version
                    toggleScoreType={toggleScoreType}
                    assessmentResultsByStudent={assessmentResultsByStudent}
                    isPrintOut={false}
                    detailedView={true}
                    studentName={studentName}
                    teacherid={teacherid}
                    schoolid={schoolid}
                    assessment={assessment}
                    studentid={studentid}
                    key={`results-${activeTab}-${allResults.length}`}

                  />
                </Tab>
              )}
            </Tabs>
          ) : (
            <Row>
              <Col>
                <p>
                  No answers registered for this student. Please rescan the
                  student to start the screener.
                </p>
              </Col>
            </Row>
          )}
        </Container>
      </div>
      {showShareCard && (
        <ShareActionCard
          cardPosition={shareCardPosition}
          handleCloseCard={() => setShowShareCard(false)}
          activeAccordionStudents={[{ id: studentid, name: studentName }]}
          lockSelection={true}
          assessmentIds={shareAssessmentIds.length > 0 ? shareAssessmentIds : [assessment?.assessmentid].filter(Boolean)}
          assessmentName={shareAssessmentName}
          className={classDetails?.name || "Class"}
          rollNumber={schoolid || "School"}
          setViewParent={setViewParent}
          setViewTeacher={setViewTeacher}
          schoolid={schoolid}
          teacherId={teacherid}
        />
      )}
      {showPrintCard && (
        <PrintActionIndividual
          cardPosition={printCardPosition}
          handleCloseCard={() => setShowPrintCard(false)}
          individualStudentResult={
            activeTab === "all" 
              ? sortedAllResults        // Use sorted version for All tab
              : activeTab === "grade1" 
                ? sortedGrade1Results   // Use sorted version for Grade 1
                : sortedGrade2Results   // Use sorted version for Grade 2
          }
          toggleScoreType={toggleScoreType}
          assessmentResultsByStudent={assessmentResultsByStudent}
          student={student}
          classDetails={classDetails}
        />
      )}
    </div>
  );
}

export default IndividualStudentResultsOverview;